/**
 * Media library API client — mirrors `backend/apps/assets/` exactly.
 *
 * Base path is `/assets/` under `/policy/api/v1`. Everything here is a thin,
 * typed wrapper over that contract; the read shape below is copied field for
 * field from `MediaAssetSerializer` (see `apps/assets/CLAUDE.md`).
 *
 * Two URL fields, two purposes — getting this wrong is the classic bug:
 *   * `url`         — direct `/policy/media/...` path. Fast (nginx), but it
 *                     CHANGES if a replace switches the stored format.
 *   * `stable_url`  — `/policy/api/public/assets/<id>/file/`. Permanent, and the
 *                     only safe way to embed an SVG (that view sends `nosniff`
 *                     plus a sandbox CSP).
 * Anything persisted into another record must use `stable_url`.
 */

import axios from 'axios';
import api from './api';

// ─── Read shapes ──────────────────────────────────────────────────────────────

export type AssetKind = 'raster' | 'vector';

/** How a page is allowed to embed the file. `img-only` == SVG. */
export type RenderHint = 'any' | 'img-only';

export interface MediaAssetVariant {
  id: string;
  url: string;
  base_width: number;
  dpr: number;
  width: number;
  height: number;
  format_key: string;
  mime_type: string;
  bytes: number;
}

export interface AssetSource {
  type: string;
  srcset: string;
}

export interface MediaAsset {
  id: string;
  // file
  url: string;
  stable_url: string;
  thumbnail_url: string;
  srcset: string;
  srcset_webp: string;
  sources: AssetSource[];
  variants: MediaAssetVariant[];
  original_filename: string;
  mime_type: string;
  extension: string;
  kind: AssetKind;
  width: number | null;
  height: number | null;
  aspect_ratio: number | null;
  bytes: number;
  original_bytes: number;
  checksum: string;
  has_alpha: boolean;
  is_animated: boolean;
  placeholder_color: string;
  render_hint: RenderHint;
  // SEO / accessibility
  alt_text: string;
  title: string;
  caption: string;
  description: string;
  tags: string[];
  // organisation + tenancy
  folder: string | null;
  folder_path: string | null;
  broker: string | null;
  broker_name: string | null;
  is_platform: boolean;
  // bookkeeping
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  usage_count: number;
  in_use: boolean;
  /** False when the asset belongs to another library — hide every write control. */
  can_edit: boolean;
  processing: Record<string, unknown> & { optimized?: boolean; saved_bytes?: number };
  variants_generated_at: string | null;
  replaced_at: string | null;
  replace_count: number;
  created_at: string;
  updated_at: string;
}

/** A replace response is an asset plus whether the direct URL moved. */
export interface ReplacedAsset extends MediaAsset {
  url_changed: boolean;
  previous_url: string;
}

export interface MediaFolder {
  id: string;
  name: string;
  description: string;
  parent: string | null;
  path: string;
  broker: string | null;
  broker_name: string | null;
  asset_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * One "this image is used here" row.
 *
 * Two producers with slightly different shapes: `AssetUsageSerializer`
 * (`GET /assets/{id}/usage/`) sends `broker`, while `usage.usages_for()` —
 * the list embedded in the delete 409 — sends `broker_id`. Both always send
 * `broker_name`, which is the only one worth rendering, so both keys are
 * optional here and neither is relied on.
 */
export interface AssetUsage {
  usage_type: string;
  object_type: string;
  object_id: string;
  object_label: string;
  field_name: string;
  broker?: string | null;
  broker_id?: string | null;
  broker_name: string | null;
  detail: Record<string, unknown>;
}

/** Coarse usage buckets the API emits, mapped to something readable. */
export const USAGE_TYPE_LABELS: Record<string, string> = {
  insurer: 'Insurance company',
  product: 'Product',
  broker: 'Broker profile',
  microsite_section: 'Website section',
  other: 'Other record',
};

export function usageTypeLabel(usageType: string): string {
  return USAGE_TYPE_LABELS[usageType] || 'Other record';
}

export interface AssetUsageResponse {
  asset: string;
  count: number;
  in_use: boolean;
  usages: AssetUsage[];
}

/** `GET /assets/config/` — never hardcode limits in the UI. */
export interface AssetConfig {
  max_upload_bytes: number;
  max_upload_mb: number;
  max_svg_bytes?: number;
  max_pixels?: number;
  accepted_extensions: string[];
  accepted_mime_types: string[];
  variant_widths: number[];
  variant_dprs: number[];
}

export interface AssetStats {
  count: number;
  total_bytes: number;
  total_mb: number;
  unused_count: number;
  by_kind: { kind: AssetKind; count: number }[];
  scope: string;
}

export interface DeleteUnusedResponse {
  deleted: string[];
  deleted_count: number;
  skipped: { id: string; reason: string; usage_count?: number }[];
}

export interface DeleteAssetResponse {
  id: string;
  files_removed: number;
  was_in_use: boolean;
}

/** Server paginator shape (`core.pagination.StandardResultsSetPagination`). */
export interface AssetPage {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: MediaAsset[];
}

// ─── List filters ─────────────────────────────────────────────────────────────

export interface AssetListParams {
  page?: number;
  page_size?: number;
  search?: string;
  /** A folder id, or the literal `'none'` for unfiled assets. */
  folder?: string;
  kind?: AssetKind | '';
  extension?: string;
  mime_type?: string;
  tag?: string;
  uploaded_by?: string;
  /** `true` = only in-use, `false` = only unused, undefined = both. */
  in_use?: boolean;
  min_width?: number;
  min_height?: number;
  /** `YYYY-MM-DD`. The API 400s on any other shape. */
  created_after?: string;
  created_before?: string;
  ordering?: string;
  /** Country admins only: `'platform'` narrows to the shared library. */
  scope?: 'platform';
  broker?: string;
}

/**
 * Drop empty values before they reach the querystring.
 *
 * This is not cosmetic. `?folder=` (empty) is falsy on the server and ignored,
 * but `?created_after=` raises a 400 ("Use a date in YYYY-MM-DD form") and
 * `?in_use=` would be read as "only unused" if we sent the empty string —
 * `_apply_filters` explicitly guards that, so the UI must not rely on it.
 * Booleans are serialised as `true`/`false` because the server's TRUTHY test
 * treats anything else as false.
 */
export function buildAssetListParams(params: AssetListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'boolean') {
      out[key] = value ? 'true' : 'false';
      continue;
    }
    if (typeof value === 'number' && Number.isNaN(value)) continue;
    out[key] = value as string | number;
  }
  return out;
}

// ─── 409 "asset in use" ───────────────────────────────────────────────────────

/** Body of the 409 a delete gets when the image is still referenced. */
export interface AssetInUseConflict {
  detail: string;
  code: 'asset_in_use';
  usage_count: number;
  usages: AssetUsage[];
  hint?: string;
}

/**
 * Recognise the delete-protection 409 so the UI can show *where* the image is
 * used instead of a generic toast. Any other error returns null.
 */
export function asAssetInUseConflict(error: unknown): AssetInUseConflict | null {
  if (!axios.isAxiosError(error)) return null;
  if (error.response?.status !== 409) return null;
  const data = error.response?.data as Partial<AssetInUseConflict> | undefined;
  if (!data || data.code !== 'asset_in_use') return null;
  return {
    detail: data.detail || 'This image is still in use.',
    code: 'asset_in_use',
    usage_count: data.usage_count ?? (data.usages?.length || 0),
    usages: data.usages || [],
    hint: data.hint,
  };
}

// ─── Client-side upload validation ────────────────────────────────────────────

export interface UploadRejection {
  reason: 'too_large' | 'bad_extension';
  message: string;
}

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
}

/**
 * Cheap pre-flight so an obviously-wrong file fails instantly instead of after
 * a 20 MB round trip. The server re-checks everything from the file's own bytes
 * (`apps/assets/imagetypes.py`) — this never replaces that, and deliberately
 * only rejects on things the server is guaranteed to reject too.
 */
export function validateUploadFile(file: File, config?: AssetConfig | null): UploadRejection | null {
  if (!config) return null;
  if (config.max_upload_bytes && file.size > config.max_upload_bytes) {
    return {
      reason: 'too_large',
      message: `Too large — the limit is ${config.max_upload_mb} MB and this file is ${formatBytes(file.size)}.`,
    };
  }
  const ext = extensionOf(file.name);
  if (config.accepted_extensions?.length && ext && !config.accepted_extensions.includes(ext)) {
    return {
      reason: 'bad_extension',
      message: `.${ext} files are not accepted. Try ${config.accepted_extensions.join(', ')}.`,
    };
  }
  return null;
}

/** Human file size. Used everywhere in the library UI. */
export function formatBytes(bytes: number | null | undefined): string {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** `1800 × 900` or an em-dash when the file has no pixel dimensions (some SVGs). */
export function formatDimensions(asset: Pick<MediaAsset, 'width' | 'height'>): string {
  if (!asset.width || !asset.height) return '—';
  return `${asset.width} × ${asset.height}`;
}

/**
 * The URL to show in a grid tile. Falls back through thumbnail → direct → stable
 * so a raster with no variants yet (`generate_variants=false`) still renders.
 */
export function thumbSrc(asset: MediaAsset): string {
  // A vector has no variants, so `thumbnail_url` falls back to the raw
  // `/policy/media/*.svg` path. Route it through `stable_url` instead: that
  // view is the one that sends `nosniff` + a sandbox CSP.
  if (asset.render_hint === 'img-only') return asset.stable_url || asset.thumbnail_url || asset.url;
  return asset.thumbnail_url || asset.url || asset.stable_url;
}

/**
 * The URL to load into a canvas / full-size preview.
 *
 * Vectors go through `stable_url` on purpose: that view is the one that sends
 * `nosniff` + sandbox CSP, so a hostile SVG cannot script the app's own origin.
 */
export function fullSrc(asset: MediaAsset): string {
  if (asset.render_hint === 'img-only') return asset.stable_url || asset.url;
  return asset.url || asset.stable_url;
}

// ─── Payloads ─────────────────────────────────────────────────────────────────

/** The only fields `PATCH /assets/{id}/` accepts. */
export interface AssetMetadataPayload {
  alt_text?: string;
  title?: string;
  caption?: string;
  description?: string;
  tags?: string[];
  folder?: string | null;
}

export interface AssetUploadPayload {
  file: File;
  folder?: string | null;
  alt_text?: string;
  title?: string;
  caption?: string;
  description?: string;
  tags?: string[];
  generate_variants?: boolean;
}

export interface AssetReplacePayload {
  file: File;
  /** Required when the new file is a different format from the old one. */
  allow_format_change?: boolean;
  generate_variants?: boolean;
}

export interface FolderPayload {
  name: string;
  description?: string;
  parent?: string | null;
}

function uploadFormData(payload: AssetUploadPayload): FormData {
  const form = new FormData();
  form.append('file', payload.file);
  if (payload.folder) form.append('folder', payload.folder);
  if (payload.alt_text) form.append('alt_text', payload.alt_text);
  if (payload.title) form.append('title', payload.title);
  if (payload.caption) form.append('caption', payload.caption);
  if (payload.description) form.append('description', payload.description);
  // DRF's ListField reads repeated keys from multipart, one append per tag.
  (payload.tags || []).forEach((tag) => form.append('tags', tag));
  if (payload.generate_variants === false) form.append('generate_variants', 'false');
  return form;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const assetsService = {
  // --- assets ---

  async list(params: AssetListParams = {}): Promise<AssetPage> {
    const { data } = await api.get<AssetPage>('/assets/', { params: buildAssetListParams(params) });
    return data;
  },

  async get(id: string): Promise<MediaAsset> {
    const { data } = await api.get<MediaAsset>(`/assets/${id}/`);
    return data;
  },

  /**
   * Upload ONE file.
   *
   * The endpoint also accepts a repeated `files` key, but the UI uploads one
   * request per file on purpose: that is the only way to get a real per-file
   * progress bar and to attribute a validation error to the file that caused
   * it. `onProgress` receives 0-100.
   */
  async upload(
    payload: AssetUploadPayload,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal,
  ): Promise<MediaAsset> {
    const { data } = await api.post<MediaAsset>('/assets/', uploadFormData(payload), {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Optimisation + variant rendering are synchronous server-side (no Celery
      // here); a 4000px JPEG takes a second or two. The default 30s client
      // timeout is too tight once a slow uplink is added on top.
      timeout: 180000,
      signal,
      onUploadProgress: (event) => {
        if (!onProgress) return;
        const total = event.total || payload.file.size;
        if (!total) return;
        // Cap at 99: the last 1% is the server processing the file, and a bar
        // that sits at 100% for two seconds reads as a hang.
        onProgress(Math.min(99, Math.round((event.loaded * 100) / total)));
      },
    });
    return data;
  },

  async updateMetadata(id: string, payload: AssetMetadataPayload): Promise<MediaAsset> {
    const { data } = await api.patch<MediaAsset>(`/assets/${id}/`, payload);
    return data;
  },

  /** Throws a 409 carrying the usage list unless `force` is set. */
  async remove(id: string, force = false): Promise<DeleteAssetResponse> {
    const { data } = await api.delete<DeleteAssetResponse>(`/assets/${id}/`, {
      params: force ? { force: 'true' } : undefined,
    });
    return data;
  },

  /** Swap the bytes and keep the id, so every page using the image updates. */
  async replace(
    id: string,
    payload: AssetReplacePayload,
    onProgress?: (percent: number) => void,
  ): Promise<ReplacedAsset> {
    const form = new FormData();
    form.append('file', payload.file);
    if (payload.allow_format_change) form.append('allow_format_change', 'true');
    if (payload.generate_variants === false) form.append('generate_variants', 'false');
    const { data } = await api.post<ReplacedAsset>(`/assets/${id}/replace/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
      onUploadProgress: (event) => {
        if (!onProgress) return;
        const total = event.total || payload.file.size;
        if (total) onProgress(Math.min(99, Math.round((event.loaded * 100) / total)));
      },
    });
    return data;
  },

  async usage(id: string): Promise<AssetUsageResponse> {
    const { data } = await api.get<AssetUsageResponse>(`/assets/${id}/usage/`);
    return data;
  },

  async regenerateVariants(id: string): Promise<{ generated: number; asset: MediaAsset }> {
    const { data } = await api.post(`/assets/${id}/regenerate-variants/`, {}, { timeout: 180000 });
    return data;
  },

  async unused(params: AssetListParams = {}): Promise<AssetPage> {
    const { data } = await api.get<AssetPage>('/assets/unused/', {
      params: buildAssetListParams(params),
    });
    return data;
  },

  /**
   * Bulk delete of unreferenced assets. In-use rows are skipped and reported,
   * never deleted — the endpoint is deliberately not forceable.
   */
  async deleteUnused(body: { ids?: string[]; folder?: string }): Promise<DeleteUnusedResponse> {
    const { data } = await api.post<DeleteUnusedResponse>('/assets/delete-unused/', body, {
      timeout: 120000,
    });
    return data;
  },

  async stats(params: AssetListParams = {}): Promise<AssetStats> {
    const { data } = await api.get<AssetStats>('/assets/stats/', {
      params: buildAssetListParams(params),
    });
    return data;
  },

  async config(): Promise<AssetConfig> {
    const { data } = await api.get<AssetConfig>('/assets/config/');
    return data;
  },

  // --- folders (unpaginated: `pagination_class = None`) ---

  async folders(params: { search?: string; parent?: string } = {}): Promise<MediaFolder[]> {
    const { data } = await api.get<MediaFolder[]>('/assets/folders/', { params });
    return Array.isArray(data) ? data : [];
  },

  async createFolder(payload: FolderPayload): Promise<MediaFolder> {
    const { data } = await api.post<MediaFolder>('/assets/folders/', payload);
    return data;
  },

  async updateFolder(id: string, payload: Partial<FolderPayload>): Promise<MediaFolder> {
    const { data } = await api.patch<MediaFolder>(`/assets/folders/${id}/`, payload);
    return data;
  },

  /** Assets survive: the server nulls their `folder`, it never cascades. */
  async deleteFolder(id: string): Promise<void> {
    await api.delete(`/assets/folders/${id}/`);
  },
};

export default assetsService;
